import { useCallback, useState } from 'react'
import type {
  ProgramBasicsData,
  ProgramBasicsDataV2,
} from '../../types/program-scheduling.types'
import type { DifficultyLevel, VideoVisibility } from '../../types/database.types'
import { useDurationPresets } from '../../hooks'
import { CustomDurationModal } from './CustomDurationModal'

// Support both legacy (ProgramBasicsData) and new (ProgramBasicsDataV2) interfaces
type BasicsData = ProgramBasicsData | ProgramBasicsDataV2

interface ProgramBasicsStepProps {
  data: BasicsData
  onChange: (data: BasicsData) => void
  showDuration?: boolean  // Enable duration selector for V2 wizard
}

const VISIBILITY_OPTIONS: { value: VideoVisibility; label: string; description: string }[] = [
  {
    value: 'private',
    label: 'Private',
    description: 'Only you and assigned clients can see this program',
  },
  {
    value: 'public',
    label: 'Public',
    description: 'Anyone can discover and duplicate this program',
  },
]

const DIFFICULTY_LEVELS: { value: DifficultyLevel | 'none'; label: string }[] = [
  { value: 'none', label: 'Not specified' },
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
]

export function ProgramBasicsStep({ data, onChange, showDuration = false }: ProgramBasicsStepProps) {
  const [isEditMode, setIsEditMode] = useState(false)
  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false)

  const {
    visibleDurations,
    hiddenDefaults,
    addCustomDuration,
    deleteCustomDuration,
    hideDefault,
    showDefault,
    canDelete,
    canHide,
  } = useDurationPresets()

  const handleFieldChange = useCallback(
    <K extends keyof BasicsData>(field: K, value: BasicsData[K]) => {
      onChange({
        ...data,
        [field]: value,
      } as BasicsData)
    },
    [data, onChange]
  )

  // Check if data has duration field (V2 interface)
  const hasV2Duration = 'duration' in data

  const handleAddCustomDuration = useCallback((duration: { label: string; weeks: number; description: string }) => {
    const newId = addCustomDuration(duration)
    // Auto-select the new duration
    handleFieldChange('duration' as keyof BasicsData, newId as BasicsData[keyof BasicsData])
  }, [addCustomDuration, handleFieldChange])

  const handleDeleteDuration = useCallback((id: string) => {
    // If this duration was selected, clear selection to default
    if ((data as ProgramBasicsDataV2).duration === id) {
      handleFieldChange('duration' as keyof BasicsData, '4_weeks' as BasicsData[keyof BasicsData])
    }

    if (canDelete(id)) {
      deleteCustomDuration(id)
    } else if (canHide(id)) {
      hideDefault(id)
    }
  }, [data, handleFieldChange, canDelete, canHide, deleteCustomDuration, hideDefault])

  return (
    <div className="space-y-6">
      {/* Program Title */}
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
          Program Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="title"
          value={data.title}
          onChange={(e) => handleFieldChange('title', e.target.value)}
          placeholder="e.g., 4-Week Strength Building"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          maxLength={100}
        />
        <p className="mt-1 text-sm text-gray-500">
          {data.title.length}/100 characters
        </p>
      </div>

      {/* Description */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          id="description"
          value={data.description}
          onChange={(e) => handleFieldChange('description', e.target.value)}
          placeholder="Describe what this program covers and who it's for..."
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
          maxLength={500}
        />
        <p className="mt-1 text-sm text-gray-500">
          {data.description.length}/500 characters
        </p>
      </div>

      {/* Visibility */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Visibility
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {VISIBILITY_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={`
                relative flex flex-col p-4 border-2 rounded-lg cursor-pointer transition-all
                ${data.visibility === option.value
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
                }
              `}
            >
              <input
                type="radio"
                name="visibility"
                value={option.value}
                checked={data.visibility === option.value}
                onChange={() => handleFieldChange('visibility', option.value)}
                className="sr-only"
              />
              <span className="flex items-center gap-2">
                {option.value === 'private' ? (
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                <span className={`font-medium ${data.visibility === option.value ? 'text-blue-700' : 'text-gray-900'}`}>
                  {option.label}
                </span>
              </span>
              <span className="text-sm text-gray-500 mt-1">
                {option.description}
              </span>
              {data.visibility === option.value && (
                <div className="absolute top-2 right-2">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </label>
          ))}
        </div>
      </div>

      {/* Difficulty Level */}
      <div>
        <label htmlFor="difficulty" className="block text-sm font-medium text-gray-700 mb-1">
          Difficulty Level
        </label>
        <select
          id="difficulty"
          value={data.difficultyLevel || 'none'}
          onChange={(e) => handleFieldChange(
            'difficultyLevel',
            e.target.value === 'none' ? null : e.target.value as DifficultyLevel
          )}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {DIFFICULTY_LEVELS.map((level) => (
            <option key={level.value} value={level.value}>
              {level.label}
            </option>
          ))}
        </select>
      </div>

      {/* Program Duration - V2 wizard only */}
      {(showDuration || hasV2Duration) && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700">
              Program Duration
            </label>
            <button
              type="button"
              onClick={() => setIsEditMode(!isEditMode)}
              className={`
                p-1.5 rounded-md transition-colors
                ${isEditMode
                  ? 'bg-blue-100 text-blue-600'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                }
              `}
              title={isEditMode ? 'Done editing' : 'Edit durations'}
            >
              {isEditMode ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              )}
            </button>
          </div>
          <p className="text-sm text-gray-500 mb-3">
            How long will this program run?
          </p>

          <div className="flex flex-wrap gap-2">
            {/* Duration chips */}
            {visibleDurations.map((option) => {
              const isSelected = (data as ProgramBasicsDataV2).duration === option.id
              return (
                <div key={option.id} className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      if (!isEditMode) {
                        handleFieldChange('duration' as keyof BasicsData, option.id as BasicsData[keyof BasicsData])
                      }
                    }}
                    className={`
                      px-4 py-2 rounded-lg border-2 text-center transition-all
                      ${isSelected
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                      }
                      ${isEditMode ? 'cursor-default' : ''}
                    `}
                  >
                    <div className="font-medium text-sm">{option.label}</div>
                    {option.description && !isEditMode && (
                      <div className="text-xs text-gray-500 mt-0.5 hidden sm:block">{option.description}</div>
                    )}
                  </button>

                  {/* Delete button in edit mode - positioned outside the main button */}
                  {isEditMode && (
                    <button
                      type="button"
                      onClick={() => handleDeleteDuration(option.id)}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-sm z-10"
                      title={option.isDefault ? 'Hide' : 'Delete'}
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              )
            })}

            {/* Add Custom button */}
            <button
              type="button"
              onClick={() => setIsCustomModalOpen(true)}
              className="px-4 py-2 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-all flex items-center gap-1.5"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="font-medium text-sm">Custom</span>
            </button>
          </div>

          {/* Restore hidden defaults */}
          {isEditMode && hiddenDefaults.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-xs text-gray-500 mb-2">Restore hidden durations:</p>
              <div className="flex flex-wrap gap-2">
                {hiddenDefaults.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => showDefault(option.id)}
                    className="px-3 py-1 text-xs rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors flex items-center gap-1"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Save as Template */}
      <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
        <input
          type="checkbox"
          id="isTemplate"
          checked={data.isTemplate}
          onChange={(e) => handleFieldChange('isTemplate', e.target.checked)}
          className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <div>
          <label htmlFor="isTemplate" className="text-sm font-medium text-gray-900 cursor-pointer">
            Save as template
          </label>
          <p className="text-sm text-gray-500">
            Templates can be quickly duplicated for new clients without recreating from scratch
          </p>
        </div>
      </div>

      {/* Custom Duration Modal */}
      <CustomDurationModal
        isOpen={isCustomModalOpen}
        onClose={() => setIsCustomModalOpen(false)}
        onSave={handleAddCustomDuration}
      />
    </div>
  )
}
